/**
 * normalizeImageFile — pre-upload JPEG normalization.
 *
 * Why: Go's stdlib `image/jpeg` decoder rejects certain chroma-subsampling
 * variants ("unsupported JPEG feature: luma/chroma subsampling ratio").
 * Cameras + phones + some editors produce these — the bytes aren't corrupt,
 * the decoder is just picky. We sidestep the limitation by re-encoding JPEGs
 * through the browser before they hit the server.
 *
 * Bonus effects from going through Canvas:
 *   - EXIF stripped (privacy)
 *   - EXIF orientation baked into pixel data (no more sideways phone photos)
 *   - Quality clamped at 0.92, often shrinking high-megapixel phone shots
 *
 * Non-JPEG inputs (PNG, WebP) are returned untouched: PNG has no subsampling
 * issue, and re-encoding PNG→JPEG would drop the alpha channel.
 */
const REENCODE_QUALITY = 0.92;
const MAX_DIM = 8192;

export async function normalizeImageFile(file: File): Promise<File> {
    const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
    if (!isJpeg) return file;

    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
        // If the browser also can't decode it, give up — let the server's
        // structured 400 surface the underlying reason to the user.
        return file;
    }

    try {
        // Hard cap on canvas dimensions — some phones produce 12000×8000
        // photos that would blow up memory on older devices. Downscale
        // proportionally if either side exceeds the cap.
        let w = bitmap.width;
        let h = bitmap.height;
        if (w > MAX_DIM || h > MAX_DIM) {
            const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, w, h);

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', REENCODE_QUALITY),
        );
        if (!blob) return file;

        // Preserve original filename, but force `.jpg` since we re-encoded.
        const safeName = file.name.replace(/\.(jpe?g|jpeg)$/i, '.jpg');
        return new File([blob], safeName, { type: 'image/jpeg', lastModified: file.lastModified });
    } finally {
        bitmap.close();
    }
}
